import type React from "react";

import { createContext, useContext, useEffect, useState } from "react";

export type SelectableTheme = Exclude<Theme, "matchSystem">;
// "matchSystem" is set externally by Pathfinder's nav; "system" is user-selected — both resolve to dark/light
export type Theme = "dark" | "light" | "matchSystem" | "system";

const resolveTheme = (value: string | undefined) => {
  if (value === "system" || value === "matchSystem") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return value;
};

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  setTheme: (theme: Theme) => void;
  theme: Theme;
};

const initialState: ThemeProviderState = {
  setTheme: () => null,
  theme: "system",
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "entitle-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme);

  useEffect(() => {
    window.document.documentElement.dataset.theme = resolveTheme(theme);
  }, [theme]);

  // Intercept direct DOM overrides by Pathfinder's top nav setting unresolved "matchSystem" on data-theme
  useEffect(() => {
    const root = window.document.documentElement;
    const observer = new MutationObserver(() => {
      const current = root.dataset.theme;
      const resolved = resolveTheme(current);
      if (resolved !== current) {
        root.dataset.theme = resolved;
      }
    });
    observer.observe(root, { attributeFilter: ["data-theme"], attributes: true });
    return () => observer.disconnect();
  }, []);

  const value = {
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    theme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
